import { useCallback, useEffect, useState } from "react";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Divider,
  Link,
  Palette,
  Stack,
  Typography,
} from "@mui/material";
import {
  AlertCircle,
  ArrowDown,
  ChevronLeft,
  ChevronRight,
  CircleCheck,
  ExternalLink,
  Pencil,
  XCircle,
} from "lucide-react";

// Internal
import { sendConfirmDecision } from "@pages/askDost/services/askDostService";
import { useChatHistory } from "@pages/askDost/hooks/useChatHistory";
import { PATH_CONFIGURATION, PATH_DASHBOARD } from "@routes/paths";

// Types
import {
  IChatBlock,
  IConfirmRequest,
  IMappingWorkflowAction,
  IMappingWorkflowCondition,
  IMappingWorkflowItem,
} from "@pages/askDost/types";
import { getOutlinedCardStyle, getSectionLabelStyle } from "@utils/cssStyles";

interface IMappingWorkflowBlockProps {
  block: IChatBlock;
  palette: Palette;
  translate: (text: string, options?: any) => string;
  onEditInstructions?: () => void;
}

const ConditionCard = ({
  condition,
  index,
  palette,
  translate,
}: {
  condition: IMappingWorkflowCondition;
  index: number;
  palette: Palette;
  translate: (text: string, options?: any) => string;
}) => (
  <Box
    sx={{
      ...getOutlinedCardStyle(palette, 1.5),
      p: 1.5,
    }}
  >
    <Typography
      variant="caption"
      sx={{
        ...getSectionLabelStyle(palette.text.label),
        mb: 0.5,
      }}
    >
      {translate("condition_n", { n: index + 1 })}
    </Typography>
    <Typography
      variant="body2"
      sx={{ fontSize: "13px", color: palette.text.primary }}
    >
      <Box
        component="span"
        sx={{ fontWeight: 600, color: palette.text.heading }}
      >
        {condition.field}
      </Box>{" "}
      <Box component="span" sx={{ color: palette.text.label }}>
        {condition.op}
      </Box>{" "}
      <Box
        component="span"
        sx={{ fontWeight: 600, color: palette.text.heading }}
      >
        {condition.value}
      </Box>
    </Typography>
  </Box>
);

const ActionCard = ({
  action,
  kicker,
  palette,
  translate,
}: {
  action: IMappingWorkflowAction;
  kicker: string;
  palette: Palette;
  translate: (text: string, options?: any) => string;
}) => (
  <Box
    sx={{
      ...getOutlinedCardStyle(palette, 1.5),
      p: 1.5,
    }}
  >
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{ mb: 0.5 }}
    >
      <Typography
        variant="caption"
        sx={getSectionLabelStyle(palette.text.label)}
      >
        {kicker}
      </Typography>
      {action.type && (
        <Typography
          variant="caption"
          sx={{
            border: `1px solid ${palette.text.borderLine}`,
            borderRadius: 999,
            px: 1,
            fontSize: "10.5px",
            fontWeight: 600,
            color: palette.text.label,
          }}
        >
          {action.type}
        </Typography>
      )}
    </Stack>
    <Typography variant="body2" sx={{ fontSize: "13px" }}>
      <Box component="span" sx={{ color: palette.text.label }}>
        {translate("action_set_prefix")}{" "}
      </Box>
      <Box
        component="span"
        sx={{ fontWeight: 600, color: palette.text.heading }}
      >
        {action.target}
      </Box>
      <Box component="span" sx={{ color: palette.text.label }}>
        {" "}
        {translate("action_to_infix")}{" "}
      </Box>
      <Box
        component="span"
        sx={{ fontWeight: 600, color: palette.text.heading }}
      >
        {action.value}
      </Box>
    </Typography>
  </Box>
);

const Connector = ({
  label,
  palette,
  translate,
}: {
  label?: "AND" | "THEN";
  palette: Palette;
  translate: (text: string, options?: any) => string;
}) => (
  <Stack alignItems="center" sx={{ my: 0.25 }}>
    <Box
      sx={{
        width: "1px",
        height: 10,
        backgroundColor: palette.text.borderLine,
      }}
    />
    {label && (
      <Typography
        variant="caption"
        sx={{
          ...getOutlinedCardStyle(palette, 999),
          px: 1.25,
          py: 0.25,
          fontWeight: 700,
          fontSize: "10.5px",
          letterSpacing: "0.08em",
          color: palette.text.label,
          display: "flex",
          alignItems: "center",
          gap: 0.5,
        }}
      >
        {translate(label === "AND" ? "and_connector" : "then_connector")}
        {label === "THEN" && <ArrowDown size={11} />}
      </Typography>
    )}
    <Box
      sx={{
        width: "1px",
        height: 10,
        backgroundColor: palette.text.borderLine,
      }}
    />
  </Stack>
);

export const MappingWorkflowBlock: React.FC<IMappingWorkflowBlockProps> = ({
  block,
  palette,
  translate,
  onEditInstructions,
}) => {
  const spec = block.mappingWorkflow;
  const [index, setIndex] = useState(0);

  const { setConfirmResolution } = useChatHistory({ translate });

  type TDecisionState = {
    isLoading: boolean;
    loadingFor: "approve" | "reject" | null;
    error: string | null;
    data: IConfirmRequest | null;
  };

  const [decisionByWriteId, setDecisionByWriteId] = useState<
    Record<string, TDecisionState>
  >(() =>
    Object.fromEntries(
      (spec?.items ?? []).map((it) => [
        it.write_id ?? block.write_id ?? "default",
        {
          isLoading: false,
          loadingFor: null,
          error: null,
          data: it.confirmResolution ?? null,
        },
      ]),
    ),
  );

  const kind = spec?.kind || "mapping";

  const handleConfirmDecision = useCallback(
    async (writeId: string, approved: boolean) => {
      setDecisionByWriteId((prev) => ({
        ...prev,
        [writeId]: {
          isLoading: true,
          loadingFor: approved ? "approve" : "reject",
          error: null,
          data: null,
        },
      }));

      try {
        const res = await sendConfirmDecision({
          decision: approved ? "approve" : "reject",
          write_id: writeId,
        });

        if (res?.status && res.status !== 200) {
          const errorMessage =
            res?.data?.detail === "That proposal is no longer pending."
              ? `no_further_action_required_${kind}`
              : res?.data?.detail || translate("unable_to_send_confirmation");
          setDecisionByWriteId((prev) => ({
            ...prev,
            [writeId]: {
              isLoading: false,
              loadingFor: null,
              error: errorMessage,
              data: null,
            },
          }));
          setConfirmResolution(block.id, res.data, writeId);
          return;
        }
        setDecisionByWriteId((prev) => ({
          ...prev,
          [writeId]: {
            isLoading: false,
            loadingFor: null,
            error: null,
            data: res.data,
          },
        }));
        setConfirmResolution(block.id, res.data, writeId);
      } catch (error) {
        console.error("Error sending confirmation:", error);
        const errorMessage =
          error instanceof Error
            ? error.message
            : translate("unable_to_send_confirmation");
        setDecisionByWriteId((prev) => ({
          ...prev,
          [writeId]: {
            isLoading: false,
            loadingFor: null,
            error: errorMessage,
            data: null,
          },
        }));
      }
    },
    [setConfirmResolution, translate, kind, block.id],
  );

  useEffect(() => {
    const items = spec?.items ?? [];
    const updates: Record<string, TDecisionState> = {};

    items.forEach((it) => {
      const writeId = it.write_id ?? block.write_id ?? "default";
      if (it.confirmResolution && !decisionByWriteId[writeId]?.data) {
        updates[writeId] = {
          isLoading: false,
          loadingFor: null,
          error: null,
          data: it.confirmResolution,
        };
      }
    });

    if (Object.keys(updates).length > 0) {
      setDecisionByWriteId((prev) => ({ ...prev, ...updates }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spec?.items, block.write_id]);

  if (!spec || !spec.items?.length) return null;

  const item: IMappingWorkflowItem =
    spec.items[Math.min(index, spec.items.length - 1)];
  const currentWriteId = item.write_id ?? block.write_id ?? "default";
  const confirmState: TDecisionState = decisionByWriteId[currentWriteId] ?? {
    isLoading: false,
    loadingFor: null,
    error: null,
    data: null,
  };

  const actKicker = translate(
    spec.kind === "mapping"
      ? "mapping_action_kicker"
      : "approval_action_kicker",
  );

  const hasConditions = item.conditions && item.conditions.length > 0;

  return (
    <Box
      sx={{
        ...getOutlinedCardStyle(palette, 2),
        overflow: "hidden",
        mb: 1,
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ p: 2, borderBottom: `1px solid ${palette.text.borderLine}` }}
      >
        <Box sx={{ minWidth: 0, pr: 2 }}>
          <Typography
            variant="subtitle2"
            noWrap
            sx={{ color: palette.text.heading }}
          >
            {item.name}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: palette.text.label, display: "block", mt: 0.25 }}
          >
            {item.orderNote}
          </Typography>
        </Box>
      </Stack>

      <Stack
        spacing={0}
        sx={{
          p: 2,
          backgroundColor:
            palette.mode === "dark"
              ? palette.grey[900]
              : palette.background.default,
        }}
      >
        {!hasConditions && (
          <Typography
            variant="caption"
            sx={{
              ...getSectionLabelStyle(palette.secondary.dark),
              mb: 1,
            }}
          >
            {translate("always")}
          </Typography>
        )}

        {item.conditions.map((condition, i) => (
          <Box key={`${condition.field}-${i}`}>
            {i > 0 && (
              <Connector label="AND" palette={palette} translate={translate} />
            )}
            <ConditionCard
              condition={condition}
              index={i}
              palette={palette}
              translate={translate}
            />
          </Box>
        ))}

        {hasConditions && (
          <Connector label="THEN" palette={palette} translate={translate} />
        )}

        {item.actions.map((action, i) => (
          <Box key={`${action.target}-${i}`}>
            {i > 0 && (
              <Connector label="AND" palette={palette} translate={translate} />
            )}
            <ActionCard
              action={action}
              kicker={
                item.actions.length > 1 ? `${actKicker} ${i + 1}` : actKicker
              }
              palette={palette}
              translate={translate}
            />
          </Box>
        ))}
      </Stack>

      {spec.items.length > 1 && (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            px: 2,
            py: 1.25,
            borderTop: `1px solid ${palette.text.borderLine}`,
          }}
        >
          <Button
            size="small"
            startIcon={<ChevronLeft size={14} />}
            disabled={index === 0}
            onClick={() => setIndex((i) => Math.max(0, i - 1))}
            sx={{ textTransform: "none", color: palette.text.primary }}
          >
            {translate("previous")}
          </Button>
          <Typography
            variant="caption"
            sx={{ color: palette.text.label, fontWeight: 500 }}
          >
            {translate("draft_n_of_total", {
              current: index + 1,
              total: spec.items.length,
            })}
          </Typography>
          <Button
            size="small"
            endIcon={<ChevronRight size={14} />}
            disabled={index === spec.items.length - 1}
            onClick={() =>
              setIndex((i) => Math.min(spec.items.length - 1, i + 1))
            }
            sx={{ textTransform: "none", color: palette.text.primary }}
          >
            {translate("next")}
          </Button>
        </Stack>
      )}

      {spec.mode === "create" && (
        <>
          <Divider />
          <Stack
            direction="row"
            justifyContent="flex-end"
            spacing={1.5}
            sx={{ p: 2 }}
          >
            {confirmState.data ? (
              <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                {confirmState.data.status === "expired" ? (
                  <Typography fontWeight={600} color={palette.text.label}>
                    {translate("proposal_expired")}
                  </Typography>
                ) : confirmState.data.status === "failed" ? (
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: "flex-start" }}
                  >
                    <AlertCircle
                      size={18}
                      color={palette.error.main}
                      style={{ marginTop: "2px", flexShrink: 0 }}
                    />
                    <Typography fontWeight={600} color="error.dark">
                      {translate("proposal_failed")}
                    </Typography>
                  </Stack>
                ) : !confirmState.data.is_error ? (
                  confirmState.data.decision === "approve" ? (
                    <Link
                      href={
                        spec.kind === "mapping"
                          ? PATH_CONFIGURATION.general.inputCSVMapping
                          : PATH_DASHBOARD.general.workflow
                      }
                      underline="hover"
                      color="success.dark"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                        fontWeight: 600,
                      }}
                    >
                      {translate("approved", {
                        confirmTool: translate(spec.kind),
                      })}
                      <ExternalLink size={16} />
                    </Link>
                  ) : (
                    <>
                      <Typography fontWeight={600} color="error.dark">
                        {translate("rejected", {
                          confirmTool: translate(spec.kind),
                        })}
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        endIcon={
                          <Pencil color={palette.primary.main} size={16} />
                        }
                        onClick={() => onEditInstructions?.()}
                      >
                        <Typography variant="body2">
                          {translate("edit_instructions")}
                        </Typography>
                      </Button>
                    </>
                  )
                ) : (
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: "flex-start" }}
                  >
                    <AlertCircle
                      size={18}
                      color={palette.error.main}
                      style={{ marginTop: "2px", flexShrink: 0 }}
                    />
                    <Typography fontWeight={600} color="error.dark">
                      {confirmState.data.results[0]?.message}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            ) : (
              <Stack
                direction="row"
                spacing={1.5}
                sx={{ justifyContent: "flex-end", alignItems: "center" }}
              >
                {confirmState.error && (
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: "flex-start" }}
                  >
                    <AlertCircle
                      size={18}
                      color={palette.error.main}
                      style={{ marginTop: "2px", flexShrink: 0 }}
                    />
                    <Typography fontWeight={600} color="error.dark">
                      {translate(confirmState.error)}
                    </Typography>
                  </Stack>
                )}

                <LoadingButton
                  variant="outlined"
                  size="small"
                  onClick={() => handleConfirmDecision(currentWriteId, false)}
                  color="error"
                  loading={
                    confirmState.isLoading &&
                    confirmState.loadingFor === "reject"
                  }
                  disabled={confirmState.isLoading || !!confirmState.error}
                  sx={{
                    minWidth: 90,
                    border: `1px solid ${palette.error.dark}`,
                    borderRadius: 1,
                    color: palette.error.dark,
                    fontSize: "14px",
                  }}
                  endIcon={
                    <XCircle
                      size={16}
                      color={
                        !!confirmState.error ? undefined : palette.error.dark
                      }
                    />
                  }
                >
                  {translate("need_changes")}
                </LoadingButton>

                <LoadingButton
                  variant="contained"
                  size="small"
                  onClick={() => handleConfirmDecision(currentWriteId, true)}
                  loading={
                    confirmState.isLoading &&
                    confirmState.loadingFor === "approve"
                  }
                  disabled={confirmState.isLoading || !!confirmState.error}
                  endIcon={<CircleCheck size={16} />}
                  sx={{
                    minWidth: 90,
                    fontSize: "14px",
                    color: palette.primary.contrastText || "#fff",
                    borderRadius: 1,
                    boxShadow: "none",
                    backgroundColor: palette.success.dark,
                    ":hover": { backgroundColor: palette.success.darker },
                  }}
                >
                  {translate("create")}
                </LoadingButton>
              </Stack>
            )}
          </Stack>
        </>
      )}
    </Box>
  );
};
